<template>
  <div class="consult-detail-page" :class="{ 'consult-detail-page--en': !isAr }">

    <section class="consult-hero">
      <div class="consult-hero__overlay" aria-hidden="true"></div>
      <b-container>
        <div class="consult-hero__inner">
          <b-link class="consult-hero__back" @click.prevent="goToRoute('consultations')">
            {{ langDec.consult_detail_back }}
          </b-link>
          <p class="consult-hero__eyebrow">{{ langDec.consult_detail_eyebrow }}</p>
          <h1 class="consult-hero__title">{{ pageTitle }}</h1>
        </div>
      </b-container>
    </section>

    <section class="consult-body">
      <b-container>
        <div v-if="pageIsLoading" class="consult-state consult-state--load">
          <b-spinner variant="primary" class="align-middle"></b-spinner>
          <p class="consult-state__text mb-0 mt-3">{{ langDec.consult_detail_loading_details }}</p>
        </div>

        <div v-else-if="loadError" class="consult-state consult-state--error">
          <p class="consult-state__title mb-2">{{ langDec.consult_detail_error_title }}</p>
          <p class="consult-state__text mb-4">{{ langDec.consult_detail_error_hint }}</p>
          <b-button variant="primary" class="consult-btn-round" @click="goToRoute('consultations')">
            {{ langDec.consult_detail_back_list_btn }}
          </b-button>
        </div>

        <b-row v-else-if="detail" class="consult-layout">
          <b-col cols="12" lg="8" class="consult-layout__main">
            <article class="consult-panel">
              <header class="consult-panel__head">
                <span class="consult-panel__accent" aria-hidden="true"></span>
                <h2 class="consult-panel__title">{{ langDec.consult_detail_section_case_meta }}</h2>
              </header>
              <div class="consult-meta-grid">
                <div v-if="metaDoctor" class="consult-meta-cell">
                  <span class="consult-meta-cell__label">{{ langDec.consult_meta_consultant }}</span>
                  <router-link
                    v-if="doctorProfileLinkId"
                    class="consult-meta-cell__value consult-meta-cell__value--link"
                    :to="{ name: 'expertProfile', params: { id: String(doctorProfileLinkId) } }"
                  >{{ metaDoctor }}</router-link>
                  <span v-else class="consult-meta-cell__value">{{ metaDoctor }}</span>
                </div>
                <div v-if="metaClassification" class="consult-meta-cell consult-meta-cell--wide">
                  <span class="consult-meta-cell__label">{{ langDec.consult_detail_meta_classification }}</span>
                  <span class="consult-meta-cell__value">{{ metaClassification }}</span>
                </div>
                <div v-if="metaDate" class="consult-meta-cell">
                  <span class="consult-meta-cell__label">{{ langDec.consult_detail_meta_date }}</span>
                  <span class="consult-meta-cell__value">{{ metaDate }}</span>
                </div>
                <div v-if="metaViews != null && metaViews !== ''" class="consult-meta-cell">
                  <span class="consult-meta-cell__label">{{ langDec.consult_meta_views }}</span>
                  <span class="consult-meta-cell__value">{{ metaViews }}</span>
                </div>
                <div v-if="metaCountry" class="consult-meta-cell">
                  <span class="consult-meta-cell__label">{{ langDec.consult_meta_country }}</span>
                  <span class="consult-meta-cell__value">{{ metaCountry }}</span>
                </div>
                <div v-if="metaAge" class="consult-meta-cell">
                  <span class="consult-meta-cell__label">{{ langDec.consult_meta_age }}</span>
                  <span class="consult-meta-cell__value">{{ metaAge }}</span>
                </div>
                <div v-if="metaGender" class="consult-meta-cell">
                  <span class="consult-meta-cell__label">{{ langDec.consult_detail_meta_gender }}</span>
                  <span class="consult-meta-cell__value">{{ metaGender }}</span>
                </div>
              </div>
            </article>

            <article class="consult-panel consult-panel--body">
              <header class="consult-panel__head">
                <span class="consult-panel__accent" aria-hidden="true"></span>
                <h2 class="consult-panel__title">{{ langDec.consult_detail_section_question }}</h2>
              </header>
              <div class="consult-body-text consultation-rich-text">{{ complaintText }}</div>
            </article>

            <article class="consult-panel consult-panel--body consult-panel--reply">
              <header class="consult-panel__head">
                <span class="consult-panel__accent consult-panel__accent--soft" aria-hidden="true"></span>
                <h2 class="consult-panel__title">{{ langDec.consult_detail_section_reply }}</h2>
              </header>

              <template v-if="answerText">
                <div class="consult-body-text consultation-rich-text">{{ answerText }}</div>
                <footer v-if="answerFooterDoctor" class="consult-answer-signature">
                  <span class="consult-answer-signature__line"></span>
                  <router-link
                    v-if="doctorProfileLinkId"
                    class="consult-answer-signature__name consult-answer-signature__name--link"
                    :to="{ name: 'expertProfile', params: { id: String(doctorProfileLinkId) } }"
                  >{{ answerFooterDoctor }}</router-link>
                  <span v-else class="consult-answer-signature__name">{{ answerFooterDoctor }}</span>
                </footer>
              </template>

              <div v-else class="consult-answer-placeholder">
                <div class="consult-placeholder-icon-wrap" aria-hidden="true">
                  <svg
                    class="consult-placeholder-svg"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"
                      stroke="#006633"
                      stroke-width="1.65"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                    <circle cx="9" cy="11.5" r="1.35" fill="#006633" />
                    <circle cx="12" cy="11.5" r="1.35" fill="#006633" />
                    <circle cx="15" cy="11.5" r="1.35" fill="#006633" />
                  </svg>
                </div>
                <p class="consult-placeholder-title mb-2">{{ langDec.consult_detail_no_reply_title }}</p>
                <p class="consult-placeholder-text mb-0">
                  {{ langDec.consult_detail_no_reply_text }}
                </p>
              </div>
            </article>
          </b-col>

          <b-col cols="12" lg="4" class="consult-layout__aside">
            <aside class="consult-aside-card">
              <h3 class="consult-aside-card__title">{{ langDec.consult_detail_sidebar_title }}</h3>
              <p class="consult-aside-card__text">
                {{ langDec.consult_detail_sidebar_body }}
              </p>
              <b-button
                variant="primary"
                block
                class="consult-btn-round mt-2"
                @click="goToRoute('contactus')"
              >
                {{ langDec.nav_contact }}
              </b-button>
              <b-button
                variant="outline-primary"
                block
                class="consult-btn-round mt-2"
                @click="goToRoute('consultations')"
              >
                {{ langDec.consult_detail_all_consultations }}
              </b-button>
            </aside>
          </b-col>
        </b-row>
      </b-container>
    </section>

    <section class="consult-footer-strip">
      <b-container>
        <div class="consult-footer-strip__inner">
          <h3 class="consult-footer-strip__title">{{ langDec.consult_detail_strip_title }}</h3>
          <p class="consult-footer-strip__text mb-0">
            {{ langDec.consult_page_blurb_body }}
          </p>
        </div>
      </b-container>
    </section>
  </div>
</template>

<script>
import axios from "axios";
import { mapGetters } from "vuex";
import { resolveDoctorProfileIdFromConsultationPayload } from "../utils/doctorProfileNav.js";

export default {
  name: "ConsultationDetail",
  data() {
    return {
      pageIsLoading: true,
      loadError: false,
      detail: null,
    };
  },

  computed: {
    ...mapGetters(["getLangData", "getlang", "authToken"]),
    langDec() {
      return this.getLangData || {};
    },
    isAr() {
      return (this.getlang || "ar") === "ar";
    },
    consultationId() {
      return this.$route.params.id;
    },
    pageTitle() {
      const dec = this.langDec || {};
      if (this.pageIsLoading) return dec.consult_detail_loading || "";
      if (this.loadError) return dec.consult_detail_title_fallback || "";
      const sub = this.detailSubjectLine(this.detail);
      return sub || dec.consult_detail_title_fallback || "";
    },
    metaDoctor() {
      return this.detailDoctor(this.detail);
    },
    metaClassification() {
      return this.detailClassification(this.detail);
    },
    metaDate() {
      return this.detailDate(this.detail);
    },
    metaViews() {
      const d = this.detail;
      if (!d) return null;
      const v = d.views ?? d.viewCount ?? d.numberOfViews ?? d.viewsCount;
      if (v === undefined || v === null || v === "") return null;
      return v;
    },
    metaCountry() {
      return this.detailCountry(this.detail);
    },
    metaAge() {
      return this.detailAge(this.detail);
    },
    metaGender() {
      return this.detailGender(this.detail);
    },
    complaintText() {
      return this.detailComplaint(this.detail);
    },
    answerText() {
      return this.detailAnswerBody(this.detail);
    },
    answerFooterDoctor() {
      const d = this.detail;
      if (!d) return "";
      const ansDoc =
        d.answerDoctorName ||
        d.respondentDoctorName ||
        "";
      const main = this.detailDoctor(d);
      const pick = ansDoc || main;
      return (pick || "").trim();
    },
    doctorProfileLinkId() {
      return this.detail ? resolveDoctorProfileIdFromConsultationPayload(this.detail) : "";
    },
  },

  watch: {
    "$route.params.id"() {
      this.fetchConsultation();
    },
  },

  created() {
    this.fetchConsultation();
  },

  methods: {
    consultationDetailUrl(id) {
      const base = process.env.VUE_APP_API_URL || "";
      return `${base}Consultation/${encodeURIComponent(id)}`;
    },
    normalizePayload(payload) {
      if (payload == null) return null;
      return payload.data !== undefined ? payload.data : payload;
    },
    htmlToPlainText(value) {
      if (value == null || value === "") return "";
      let s = String(value);
      s = s.replace(/<br\s*\/?>/gi, "\n");
      s = s.replace(/<\/p>/gi, "\n");
      s = s.replace(/<[^>]+>/g, " ");
      s = s.replace(/\s+\n/g, "\n");
      s = s.replace(/[ \t]{2,}/g, " ");
      s = s.replace(/\n{3,}/g, "\n\n");
      return s.trim();
    },
    detailSubjectLine(d) {
      if (!d) return "";
      return this.htmlToPlainText(
        d.subject || d.questionTitle || d.title || d.consultationTitle || ""
      ).trim();
    },
    detailDoctor(d) {
      if (!d) return "";
      const inner = d.doctor || d.expert || d.consultant;
      const name =
        d.doctorName ||
        d.consultantName ||
        d.expertName ||
        d.answeredByName ||
        d.doctorFullName ||
        (typeof inner === "object" && inner
          ? inner.fullName || inner.name || inner.doctorName
          : "");
      return String(name || "").trim();
    },
    detailClassification(d) {
      if (!d) return "";
      const raw =
        d.categories ||
        d.specializations ||
        d.interests ||
        d.categoryNames ||
        d.tags ||
        [];
      const list = Array.isArray(raw) ? raw : [];
      const labels = list
        .map((t) => (typeof t === "string" ? t : t.name || t.title || t.interestName || ""))
        .filter(Boolean);
      const sep = this.isAr ? "، " : ", ";
      return labels.join(sep);
    },
    detailDate(d) {
      if (!d) return "";
      const raw =
        d.createdAt ||
        d.publishDate ||
        d.publishDateTime ||
        d.publishedAt ||
        d.answerDate ||
        d.updatedAt;
      if (!raw) return "";
      const dt = new Date(raw);
      if (Number.isNaN(dt.getTime())) return String(raw);
      const loc = this.isAr ? "ar-EG" : "en-US";
      return dt.toLocaleDateString(loc, {
        year: "numeric",
        month: "long",
        day: "numeric",
      });
    },
    detailCountry(d) {
      if (!d) return "";
      const c = d.country;
      if (typeof c === "string") return c.trim();
      if (c && typeof c === "object") return String(c.name || c.countryName || "").trim();
      return String(d.countryName || "").trim();
    },
    detailAge(d) {
      if (!d) return "";
      const a = d.age ?? d.patientAge ?? d.ageYears;
      if (a === undefined || a === null || a === "") return "";
      const suf = (this.langDec && this.langDec.consult_detail_age_suffix) || "";
      return `${a} ${suf}`.trim();
    },
    detailGender(d) {
      if (!d) return "";
      const g = d.gender ?? d.sex ?? d.patientGender;
      if (g === undefined || g === null || g === "") return "";
      const s = String(g).toLowerCase();
      const male = (this.langDec && this.langDec.consult_detail_gender_male) || "Male";
      const female = (this.langDec && this.langDec.consult_detail_gender_female) || "Female";
      if (s === "female" || s === "f" || g === 2 || String(g) === "أنثى") return female;
      if (s === "male" || s === "m" || g === 1 || String(g) === "ذكر") return male;
      return String(g);
    },
    detailComplaint(d) {
      if (!d) return "";
      const q =
        d.question ||
        d.complaint ||
        d.complaintText ||
        d.consultationBody ||
        "";
      let text = this.htmlToPlainText(q).trim();
      const sub = this.detailSubjectLine(d);
      if (sub && text.startsWith(sub)) {
        text = text.slice(sub.length).trim().replace(/^[،,.]/, "").trim();
      }
      return text || this.htmlToPlainText(d.description || d.summary || "").trim() || "—";
    },
    detailAnswerBody(d) {
      if (!d) return "";
      return this.htmlToPlainText(
        d.answer || d.reply || d.response || d.answerText || ""
      ).trim();
    },
    fetchConsultation() {
      const id = this.consultationId;
      if (!id) {
        this.loadError = true;
        this.pageIsLoading = false;
        return;
      }
      this.pageIsLoading = true;
      this.loadError = false;
      this.detail = null;
      const token = this.authToken;
      const config = {};
      if (token) {
        config.headers = { authorization: `Bearer ${token}` };
      }
      axios
        .get(this.consultationDetailUrl(id), config)
        .then((response) => {
          this.detail = this.normalizePayload(response.data);
        })
        .catch(() => {
          this.loadError = true;
          this.detail = null;
        })
        .finally(() => {
          this.pageIsLoading = false;
        });
    },
    goToRoute(name, id, hash) {
      const payload = { name };
      if (id != null && id !== "") payload.params = { id: String(id) };
      if (hash) payload.hash = hash;
      this.$router.push(payload).catch(() => {});
    },
  },
};
</script>

<style scoped>
  .consult-detail-page {
    background: #f4f7f5;
    min-height: 100vh;
  }

  /* Hero */
  .consult-hero {
    position: relative;
    min-height: 240px;
    display: flex;
    align-items: center;
    background: #006633 url(../assets/images/heroGreenBG.png) center / cover no-repeat;
    color: #fff;
  }

  .consult-hero__overlay {
    position: absolute;
    inset: 0;
    background: linear-gradient(
      120deg,
      rgba(0, 38, 19, 0.88) 0%,
      rgba(0, 102, 51, 0.72) 42%,
      rgba(0, 102, 51, 0.45) 100%
    );
  }

  .consult-hero__inner {
    position: relative;
    z-index: 1;
    padding: 2.25rem 0 2.5rem;
    text-align: center;
    max-width: 920px;
    margin: 0 auto;
  }

  .consult-hero__back {
    display: inline-block;
    color: rgba(255, 255, 255, 0.88) !important;
    font-size: 0.9rem;
    margin-bottom: 1rem;
    text-decoration: none !important;
    border-bottom: 1px solid transparent;
    transition: color 0.2s ease, border-color 0.2s ease;
    cursor: pointer;
  }

  .consult-hero__back:hover {
    color: #fff !important;
    border-bottom-color: rgba(255, 255, 255, 0.55);
  }

  .consult-hero__eyebrow {
    font-size: 0.82rem;
    font-weight: 600;
    opacity: 0.92;
    margin-bottom: 0.5rem;
  }

  .consult-hero__title {
    margin: 0;
    font-size: clamp(1.35rem, 4vw, 2rem);
    font-weight: 800;
    line-height: 1.45;
    word-break: break-word;
    text-shadow: 0 2px 14px rgba(0, 0, 0, 0.18);
  }

  /* Body layout */
  .consult-body {
    padding: 2.5rem 0 3rem;
  }

  .consult-layout__main {
    padding-bottom: 1rem;
  }

  .consult-layout__aside {
    padding-bottom: 1rem;
  }

  /* عمودان من lg فما فوق؛ كان xl فقط فيختفي الـ sticky بين 992px و 1199px */
  @media (min-width: 992px) {
    .consult-aside-card {
      position: sticky;
      top: var(--app-sticky-top, calc(var(--app-navbar-offset, 5.35rem) + 1rem));
      z-index: 2;
    }
  }

  /* States */
  .consult-state {
    text-align: center;
    padding: 3.5rem 1.5rem;
    background: #fff;
    border-radius: 16px;
    border: 1px solid rgba(0, 102, 51, 0.1);
    box-shadow: 0 8px 32px rgba(0, 102, 51, 0.06);
  }

  .consult-state__title {
    font-size: 1.2rem;
    font-weight: 700;
    color: #1a1d1f;
  }

  .consult-state__text {
    color: #6c757d;
    font-size: 0.95rem;
  }

  /* Panels */
  .consult-panel {
    background: #fff;
    border-radius: 16px;
    padding: 1.5rem 1.6rem;
    margin-bottom: 1.25rem;
    border: 1px solid rgba(0, 102, 51, 0.09);
    box-shadow: 0 6px 28px rgba(0, 102, 51, 0.07);
  }

  .consult-panel--body {
    padding-bottom: 1.65rem;
  }

  .consult-panel--reply {
    background: linear-gradient(180deg, #ffffff 0%, #fafcfa 100%);
  }

  .consult-panel__head {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    margin-bottom: 1.2rem;
    padding-bottom: 0.95rem;
    border-bottom: 1px solid rgba(0, 0, 0, 0.06);
  }

  .consult-panel__accent {
    width: 5px;
    min-height: 26px;
    border-radius: 6px;
    background: linear-gradient(180deg, #006633 0%, #0d8049 100%);
    flex-shrink: 0;
  }

  .consult-panel__accent--soft {
    opacity: 0.75;
  }

  .consult-panel__title {
    margin: 0;
    font-size: 1.2rem;
    font-weight: 700;
    color: #1a1d1f;
  }

  .consult-meta-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(148px, 1fr));
    gap: 0.85rem;
  }

  .consult-meta-cell {
    background: rgba(0, 102, 51, 0.045);
    border: 1px solid rgba(0, 102, 51, 0.11);
    border-radius: 12px;
    padding: 0.75rem 0.9rem;
    text-align: start;
  }

  .consult-meta-cell--wide {
    grid-column: 1 / -1;
  }

  @media (max-width: 575.98px) {
    .consult-meta-cell--wide {
      grid-column: auto;
    }
  }

  .consult-meta-cell__label {
    display: block;
    font-size: 0.7rem;
    font-weight: 600;
    color: rgba(0, 102, 51, 0.62);
    margin-bottom: 0.3rem;
    letter-spacing: 0.02em;
  }

  .consult-meta-cell__value {
    font-size: 0.92rem;
    font-weight: 600;
    color: #2d3436;
    line-height: 1.45;
    word-break: break-word;
  }

  .consult-meta-cell__value--link {
    color: #006633 !important;
    cursor: pointer;
    text-decoration: underline;
    text-underline-offset: 3px;
  }

  a.consult-meta-cell__value--link:hover,
  a.consult-meta-cell__value--link:focus {
    color: #004728 !important;
    outline: none;
    text-decoration: underline;
  }

  .consult-body-text {
    font-size: 1.02rem;
    line-height: 1.85;
    color: #3a4145;
    white-space: pre-line;
  }

  .consultation-rich-text {
    white-space: pre-line;
    line-height: 1.85;
  }

  .consult-answer-signature {
    margin-top: 1.75rem;
    padding-top: 1.25rem;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 0.5rem;
  }

  .consult-answer-signature__line {
    width: 56px;
    height: 3px;
    border-radius: 3px;
    background: linear-gradient(90deg, #006633, #0d8049);
    opacity: 0.85;
  }

  .consult-answer-signature__name {
    font-size: 1rem;
    font-weight: 700;
    color: #006633;
  }

  .consult-answer-signature__name--link {
    cursor: pointer;
    text-decoration: underline;
    text-underline-offset: 3px;
  }

  .consult-answer-signature__name--link:hover,
  .consult-answer-signature__name--link:focus {
    color: #004728 !important;
    outline: none;
  }

  a.consult-answer-signature__name--link {
    text-decoration: underline;
    text-underline-offset: 3px;
  }

  /* Placeholder */
  .consult-answer-placeholder {
    text-align: center;
    padding: 2rem 1rem 1.5rem;
    background: rgba(0, 102, 51, 0.03);
    border-radius: 12px;
    border: 1px dashed rgba(0, 102, 51, 0.18);
  }

  .consult-placeholder-icon-wrap {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 84px;
    height: 84px;
    border-radius: 50%;
    background: linear-gradient(145deg, rgba(0, 102, 51, 0.14) 0%, rgba(0, 102, 51, 0.06) 100%);
    border: 1px solid rgba(0, 102, 51, 0.18);
    margin-bottom: 1rem;
  }

  .consult-placeholder-svg {
    width: 46px;
    height: 46px;
    display: block;
    overflow: visible;
  }

  .consult-placeholder-title {
    font-size: 1.1rem;
    font-weight: 700;
    color: #006633;
  }

  .consult-placeholder-text {
    color: #5c636a;
    font-size: 0.95rem;
    line-height: 1.75;
    max-width: 420px;
    margin-inline: auto;
  }

  /* Sidebar */
  .consult-aside-card {
    background: #fff;
    border-radius: 16px;
    padding: 1.6rem 1.45rem;
    border: 1px solid rgba(0, 102, 51, 0.1);
    box-shadow: 0 8px 30px rgba(0, 102, 51, 0.08);
    height: fit-content;
  }

  .consult-aside-card__title {
    font-size: 1.15rem;
    font-weight: 700;
    color: #006633;
    margin-bottom: 0.75rem;
    text-align: start;
  }

  .consult-aside-card__text {
    font-size: 0.92rem;
    line-height: 1.7;
    color: #5c636a;
    text-align: start;
    margin-bottom: 0.5rem;
  }

  .consult-btn-round {
    border-radius: 10px !important;
    font-weight: 600 !important;
    padding-top: 0.55rem !important;
    padding-bottom: 0.55rem !important;
  }

  /* Footer strip */
  .consult-footer-strip {
    background: linear-gradient(180deg, #eef3ef 0%, #e4ebe6 100%);
    border-top: 1px solid rgba(0, 102, 51, 0.1);
    padding: 2.25rem 0;
  }

  .consult-footer-strip__inner {
    text-align: center;
    max-width: 720px;
    margin: 0 auto;
  }

  .consult-footer-strip__title {
    font-size: 1.35rem;
    font-weight: 700;
    color: #006633;
    margin-bottom: 0.65rem;
  }

  .consult-footer-strip__text {
    font-size: 0.95rem;
    line-height: 1.75;
    color: #4a5258;
  }

  @media (max-width: 991.98px) {
    .consult-hero {
      min-height: 200px;
    }

    .consult-body {
      padding: 1.75rem 0 2.25rem;
    }

    .consult-panel {
      padding: 1.25rem 1.2rem;
    }
  }

  @media (max-width: 767.98px) {
    .consult-hero__inner {
      padding: 1.75rem 0 2rem;
    }

    .consult-meta-grid {
      grid-template-columns: 1fr;
    }
  }
</style>
